import { EVENTS, VALIDATION_STATUSES, ELEMENT_FRAMES } from "../../constants";
import { isValid, isFieldValid } from "../../utils/validation";
import {
  isQaPublicKey,
  isSandboxPublicKey,
  sendViaPostMessage,
  sendToElementIframe,
} from "../../utils";
import Cardholder from "../../utils/cardholder";
import { snakeCaseToText } from "../../utils/string";
import { tokensApiUrl } from "../../config";
import { iOSFocusFix } from "../../utils/bugFixes";
import AppLogger from "../../utils/appLogger";
import { isIE11 } from "../../utils/browser";

const { EMPTY, INVALID, VALID } = VALIDATION_STATUSES;

function setInputManualUseOnly(element) {
  const stub = "return false";
  element.setAttribute("onpaste", stub);
  element.setAttribute("onselectstart", stub);
  element.setAttribute("onCopy", stub);
  element.setAttribute("onCut", stub);
  element.setAttribute("onDrag", stub);
  element.setAttribute("onDrop", stub);
  element.setAttribute("autocomplete", "off");
}

function getNewValidationStatus(inputValue, validator, ...args) {
  if (validator(...args)) {
    return VALID;
  } else if (inputValue === "") {
    return EMPTY;
  }
  return INVALID;
}

function getElementClasses(prefix) {
  return {
    hover: `${prefix}--hover`,
    focus: `${prefix}--focus`,
    invalid: `${prefix}--invalid`,
    valid: `${prefix}--valid`,
  };
}

function setOnMouseOverClasses(element, prefix) {
  const { hover } = getElementClasses(prefix);
  element.classList.add(hover);
}

function setOnMouseOutClasses(element, prefix) {
  const { hover } = getElementClasses(prefix);
  element.classList.remove(hover);
}

function setOnFocusClasses(element, prefix) {
  const { focus } = getElementClasses(prefix);
  element.classList.add(focus);
}

function setAriaInvalidAttribute(element, validationStatus) {
  const isInvalid = validationStatus === INVALID;
  element.setAttribute("aria-invalid", isInvalid.toString());
}

function setOnBlurClasses(element, prefix, state) {
  const { classList } = element;
  const { focus, invalid, valid } = getElementClasses(prefix);

  classList.remove(focus);
  switch (state.validationStatus) {
    case EMPTY:
      classList.remove(valid);
      classList.remove(invalid);
      break;
    case VALID:
      classList.add(valid);
      classList.remove(invalid);
      break;
    case INVALID:
      classList.remove(valid);
      classList.add(invalid);
      break;
  }
}

function onMessage(elementName, onReceiveMessage) {
  return function (event) {
    if (!isSameOriginAndReceiver(event, elementName)) return;
    onReceiveMessage(event);
  };
}

function isSameOriginAndReceiver(event, name) {
  return isSameOrigin(event) && isSameReceiver(event, name);
}

function isSameOrigin(event) {
  return event.origin === window.location.origin;
}
function isSameReceiver(event, name) {
  return event.data.receiver === name;
}

function isSchemeChoiceProvidedAndValid(schemeProvided, schemeChoice) {
  return (
    (!schemeProvided &&
      schemeChoice.validationStatus === VALIDATION_STATUSES.EMPTY) ||
    isFieldValid(schemeChoice)
  );
}

function checkFormValidity(ctx) {
  // Post validation changed
  const {
    cardNumber,
    expiryDate,
    cvv,
    cvvOptional,
    cvvHidden,
    schemeChoice,
    isSchemeChoiceEnabled,
  } = ctx.state;
  const { INVALID, VALID } = VALIDATION_STATUSES;

  const cardNumberValid = isFieldValid(cardNumber);
  const expiryDateValid = isFieldValid(expiryDate);
  const schemeChoiceValid = isSchemeChoiceProvidedAndValid(
    isSchemeChoiceEnabled,
    schemeChoice
  );

  // Make CVV optional for "Pay out to a card"
  const cvvValid =
    ((cvvOptional || cvvHidden) &&
      cvv.validationStatus === VALIDATION_STATUSES.EMPTY) ||
    isFieldValid(cvv);

  if (cardNumberValid && expiryDateValid && cvvValid && schemeChoiceValid) {
    ctx.state.validationStatus = VALID;
  } else {
    ctx.state.validationStatus = INVALID;
  }

  if (ctx.state.validationStatus === ctx.state.previousValidationStatus) {
    return;
  }

  ctx.state.previousValidationStatus = ctx.state.validationStatus;
  const isElementValid = {
    cardNumber: cardNumberValid,
    expiryDate: expiryDateValid,
    cvv: cvvValid,
    schemeChoice: schemeChoiceValid,
  };
  const payload = {
    isValid: isValid(ctx.state.validationStatus),
    isElementValid,
  };
  sendViaPostMessage(EVENTS.CARD_VALIDATION_CHANGED, payload);
}

function getCardData() {
  const { config, cardNumber, cvv, expiryDate, schemeChoice } = this.state;

  const cardholder = new Cardholder(
    config.cardholder.name,
    config.cardholder.billingAddress,
    config.cardholder.phone
  );

  const { name, billing_address, phone } = cardholder.getSnakeCase();

  if (!expiryDate.value) {
    expiryDate.value = "00/00";
  }

  const [month, year] = expiryDate.value.split("/");
  const data = {
    type: "card",
    number: cardNumber.value,
    expiry_month: parseInt(month, 10),
    expiry_year: parseInt(`20${year}`, 10),
    cvv: cvv.value,
    name: name,
    billing_address: billing_address,
    phone: phone,
    preferred_scheme: schemeChoice.value,
    requestSource: "JS",
  };

  if (!cvv.value) {
    delete data.cvv;
  }

  return data;
}

function getInputEmptyState() {
  return {
    validationStatus: VALIDATION_STATUSES.EMPTY,
    value: "",
    loaded: false,
  };
}

/**
 * submitForm is called by Controller and Form.
 * The execution context must be passed when called
 */
function submitForm(data, appUrl, onCardTokenized) {
  const { cardholder } = data;
  const { formSubmitting, validationStatus, schemeChoice } = this.state;
  if (formSubmitting || !isValid(validationStatus)) return;

  const appLogger = new AppLogger(appUrl);

  // signal that the form is submitting, prevent multiple submission at the same time
  // using the same value to prevent from creating multiple card token
  this.state.formSubmitting = true;
  this.state.config.cardholder = cardholder;

  sendViaPostMessage(EVENTS.CARD_SUBMITTED, {});
  const { publicKey } = this.state.config;
  logCardSubmittedEvent(appLogger, publicKey);

  const xhttp = new XMLHttpRequest();
  xhttp.open("POST", getTokensApiUrl(publicKey), true);
  xhttp.setRequestHeader("Content-Type", "application/json");
  xhttp.setRequestHeader("Authorization", publicKey);

  xhttp.onreadystatechange = () => {
    if (xhttp.readyState === 4) {
      const response = JSON.parse(xhttp.response);
      if (xhttp.status === 201) {
        onCardTokenized(response.token).finally(() => {
          // success
          // we don't set formSubmitting to false because it's a success
          sendViaPostMessage(EVENTS.CARD_TOKENIZED, {
            ...response,
            preferred_scheme: schemeChoice.value,
          });
        });
      } else {
        // set formSubmitting to false to allow retries
        this.state.formSubmitting = false;
        sendViaPostMessage(
          EVENTS.CARD_TOKENIZATION_FAILED,
          parseResponseError(response)
        );
      }
    }
  };

  xhttp.send(JSON.stringify(this.cardData));
}

const logCardSubmittedEvent = (appLogger, publicKey) => {
  appLogger.log({
    publicKey,
    referrer: document.referrer,
    eventName: EVENTS.CARD_SUBMITTED,
    timestamp: new Date().getTime(),
  });
};

const parseResponseError = (response) => {
  const { error_codes, error_type } = response;
  const errorCode = hasValues(error_type) ? snakeCaseToText(error_type) : "";

  const message = hasValues(error_codes)
    ? error_codes.map(snakeCaseToText).join(" ").trim()
    : "";

  return { errorCode, message };
};

const hasValues = (obj) =>
  !!obj && obj.length > 0 && notArrayOrAnyTruthyEl(obj);

const notArrayOrAnyTruthyEl = (arr) =>
  !Array.isArray(arr) || arr.some((prop) => !!prop);

function registerFocusHandler(input, onFocus) {
  input.addEventListener("focus", onFocus);

  if (isIOSDevice()) {
    iOSFocusFix(onFocus);
  }
}

function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function getTokensApiUrl(publicKey) {
  if (isQaPublicKey(publicKey)) {
    return tokensApiUrl.QA;
  }

  if (isSandboxPublicKey(publicKey)) {
    return tokensApiUrl.SBOX;
  }

  return tokensApiUrl.PROD;
}

function isActiveElement(element) {
  return document.activeElement === element;
}

/**
 * When typing with chinese characters in IE11 the cursor can end up in the
 * incorrect position when we programmatically modify the value i.e. adding
 * spaces (card number) or forward slashes (expiry date). So manually set it
 * to the end of the value here to mitigate those inconsistencies
 */
function fixIE11InputCursor(input) {
  if (!isIE11) return;
  if (!isActiveElement(input)) return;

  const cursorPos = input.value.length;

  input.setSelectionRange(cursorPos, cursorPos);
}

/**
 * In IE11 the `input` event is triggered at times you may not expect for input elements:
 *  - when there is any change to the visual content of the field, this includes both keyboard inputting
 *    but also when the placeholder appears/disappears i.e. when a field with no content is focused/blurred
 *    or the placeholder is programmatically changed
 * @param {Function} onInput input event handler
 */
function wrapOnInputForIE11(onInput) {
  if (!isIE11) return onInput;

  return function (evt) {
    const target = evt.target;
    const isActive = isActiveElement(target);

    if (!isActive) {
      if (target.placeholderHasDisappeared) {
        target.placeholderHasDisappeared = false;
      }

      return blockEvent(evt);
    }

    if (target.placeholder && !target.placeholderHasDisappeared) {
      target.placeholderHasDisappeared = true;
      return blockEvent(evt);
    }

    return onInput(evt);
  };
}

const INSERT_COMPOSITION_TEXT_INPUT_TYPE = "insertCompositionText";

/**
 * When a user is inputting full-width characters from a language that utilises an Input Method Editor (IME)
 * the input component will emit composition events (`compositionstart`, `compositionupdate` and `compositionend`)
 * when the user starts, updates or stops composing their input. During this composition process the `input` event listener
 * will receive events for the composition text that the user is inputting. This `input` event will have the
 * `inputType` property set to "insertCompositionText" which we can ignore from our processing so that we only
 * format and sanitise the final input the user wants to submit
 *
 * @param {Function} onInputChange
 * @returns InputEventHandler
 */
function wrapOnInputForCompositionText(onInputChange) {
  return (event) => {
    if (event.inputType === INSERT_COMPOSITION_TEXT_INPUT_TYPE) return;

    onInputChange(event);
  };
}

function blockEvent(evt) {
  evt.stopPropagation();
  evt.preventDefault();
  return false;
}

function onHiddenInputChange(event) {
  const { value, id } = event.target;
  const elementNames = {
    "cc-number-hidden": ELEMENT_FRAMES.CARD_NUMBER,
    "cc-exp-hidden": ELEMENT_FRAMES.EXPIRY_DATE,
    "cc-csc-hidden": ELEMENT_FRAMES.CVV,
  };

  sendToElementIframe(elementNames[id], {
    message: "autofilled",
    value,
  });
}

function autofill(input) {
  if (!input.isAutofilled) {
    input.isAutofilled = true;
    input.classList.add("field--autofilled");
  }
}

function cancelAutofill(input) {
  if (input.isAutofilled) {
    input.isAutofilled = false;
    input.classList.remove("field--autofilled");

    // if parent is a .frames-element form, reset it, but keep current input value
    const { parentElement, value } = input;

    if (
      parentElement.tagName === "FORM" &&
      parentElement.classList.contains("frames-element")
    ) {
      parentElement.reset();
      input.value = value;
    }
  }
}

function onAutofillInput(event) {
  const { target } = event;

  if ("data" in event || !target.value) {
    cancelAutofill(target);
  } else {
    // input events without data are autofills
    autofill(target);
  }
}

function simulateAutofill(input, value) {
  if (value) {
    input.value = value;
    input.dispatchEvent(new Event("input"));
    input.dispatchEvent(new Event("blur"));
  }
}

function parseAndStyleEnvironment(envString) {
  const env = parseEncodedStringifiedObject(envString);

  // We are making sure sbox and prod environments are not styled
  // The ribbon should not be shown in these environments
  if (
    env.DEPLOY_ENV &&
    env.DEPLOY_ENV !== "PROD" &&
    env.DEPLOY_ENV !== "SBOX"
  ) {
    sendViaPostMessage("styleEnvironment", env);
  }

  return env;
}

function parseEncodedStringifiedObject(str) {
  if (typeof str !== "string") {
    return {};
  }

  try {
    return JSON.parse(decodeURIComponent(str));
  } catch (error) {
    return {};
  }
}

const getCaretPosition = (node) => {
  if ("selectionStart" in node) {
    return node.selectionStart;
  }
  return 0;
};

/**
 * The iframe window is focused when navigating through the form using a keyboard in some desktop browsers
 * (Firefox/IE11/Safari), or in mobile browsers.
 *
 * Returns the handler for this focus event, which:
 * - enables the backward and forward navigation inputs to allow the user to navigate to the previous or next focusable
 *   element on the page
 * - redirects the focus to the main visible input in the iframe
 *
 * @param {HTMLInputElement} mainInput - The main visible input for the field
 * @param {HTMLInputElement} backwardNavInput - The hidden input that redirects focus to the previous focusable element
 * @param {HTMLInputElement} forwardNavInput - The hidden input that redirects focus to the next focusable element
 * @returns {(event: FocusEvent) => void} - The event handler for the iframe window's focus event
 */
function onIframeWindowFocus(mainInput, backwardNavInput, forwardNavInput) {
  return (event) => {
    event.preventDefault();

    if (backwardNavInput) backwardNavInput.disabled = false;
    if (forwardNavInput) forwardNavInput.disabled = false;

    mainInput.focus();
    // The piece of code below is needed to focus the input in Safari.
    // The usual .focus() does not work because Safari is waiting
    // for the initial interaction with the document (iframe)
    try {
      if (mainInput.setSelectionRange && document.activeElement !== mainInput) {
        const caretPosition = getCaretPosition(mainInput);

        mainInput.setSelectionRange(caretPosition, caretPosition);
      }
    } catch (e) {
      // noop
    }
  };
}

/**
 * Returns the handler for the iframe window's blur event, which disables the backward and forward navigation inputs.
 *
 * @param {HTMLInputElement} backwardNavInput The hidden input that redirects focus to the previous focusable element
 * @param {HTMLInputElement} forwardNavInput The hidden input that redirects focus to the next focusable element
 * @returns {(event: FocusEvent) => void} - The event handler for the iframe window's blur event
 */
function onIframeWindowBlur(backwardNavInput, forwardNavInput) {
  return () => {
    if (backwardNavInput) backwardNavInput.disabled = true;
    if (forwardNavInput) forwardNavInput.disabled = true;
  };
}

/**
 * Returns the handler for the backward navigation input's focus event, which sends a 'navigate-document-focus-list'
 * event to the parent window, that will focus the previous focusable element on the page
 *
 * @param {string} element - The name of the element this backward navigation input is associated with
 * @returns {(event: FocusEvent) => void} - The event handler for the backward navigation input's focus event
 */
function onBackwardNavInputFocus(element) {
  return (event) => {
    event.preventDefault();

    sendViaPostMessage("navigate-document-focus-list", {
      from: element,
      direction: -2,
    });
  };
}

/**
 * Returns the handler for the forward navigation input's focus event, which sends a 'navigate-document-focus-list'
 * event to the parent window, that will focus the next focusable element on the page
 *
 * @param {string} element - The name of the element this forward navigation input is associated with
 * @returns {(event: FocusEvent) => void} - The event handler for the forward navigation input's focus event
 */
function onForwardNavInputFocus(element) {
  return (event) => {
    event.preventDefault();

    sendViaPostMessage("navigate-document-focus-list", {
      from: element,
      direction: 1,
    });
  };
}

/**
 * Returns whether the frame event is of an element event type
 *
 * @param {object} e - The frame event
 * @param {string} propertyToCheck - check for property's presence on data
 * @returns {boolean} - Is event of element event type
 */
function isElementEvent(e, propertyToCheck = "event") {
  // same origin & contains event key, meaning it's an element
  return (
    e.origin === window.location.origin &&
    !!e.data &&
    !!e.data.data &&
    !!e.data.data[propertyToCheck]
  );
}

export {
  getNewValidationStatus,
  setOnMouseOverClasses,
  setOnMouseOutClasses,
  setOnFocusClasses,
  setOnBlurClasses,
  onMessage,
  checkFormValidity,
  getCardData,
  getInputEmptyState,
  submitForm,
  registerFocusHandler,
  isIOSDevice,
  fixIE11InputCursor,
  wrapOnInputForIE11,
  wrapOnInputForCompositionText,
  onHiddenInputChange,
  onAutofillInput,
  simulateAutofill,
  parseAndStyleEnvironment,
  onIframeWindowFocus,
  onIframeWindowBlur,
  onBackwardNavInputFocus,
  onForwardNavInputFocus,
  setInputManualUseOnly,
  isElementEvent,
  setAriaInvalidAttribute,
};
